import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  ScrollText, 
  User,
  FileText,
  Settings,
  Shield,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ENTITY_TYPES = [
  { value: '', label: 'All Entities' },
  { value: 'user', label: 'Users' },
  { value: 'post', label: 'Posts' },
  { value: 'granth', label: 'Granths' },
  { value: 'admin', label: 'Admins' },
  { value: 'settings', label: 'Settings' },
  { value: 'temple', label: 'Temples' }
];

const ACTIONS = [
  { value: '', label: 'All Actions' },
  { value: 'login', label: 'Login' },
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
  { value: 'verify_pandit', label: 'Verify Pandit' },
  { value: 'user_suspended', label: 'User Suspended' }
];

const getEntityIcon = (type) => {
  const icons = {
    user: User,
    post: FileText,
    admin: Shield,
    settings: Settings,
    granth: ScrollText
  };
  return icons[type] || ScrollText;
};

const getActionColor = (action) => {
  const colors = {
    login: 'bg-blue-100 text-blue-700',
    create: 'bg-green-100 text-green-700',
    update: 'bg-yellow-100 text-yellow-700',
    delete: 'bg-red-100 text-red-700',
    verify_pandit: 'bg-purple-100 text-purple-700',
    user_suspended: 'bg-orange-100 text-orange-700'
  };
  return colors[action] || 'bg-slate-100 text-slate-700';
};

export default function AdminLogsPage({ admin }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    fetchLogs();
  }, [page, entityType, action]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('dharmbook_admin_token');
      const params = new URLSearchParams({ page: page.toString(), limit: '30' });
      if (entityType) params.append('entity_type', entityType);
      if (action) params.append('action', action);

      const response = await axios.get(`${API}/admin/audit-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setLogs(response.data);
      setHasMore(response.data.length === 30);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Audit Logs</h1>
        <p className="text-slate-500">Track all administrative actions</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-slate-400" />
          <span className="text-sm text-slate-600">Filter by:</span>
        </div>
        
        <select
          value={entityType}
          onChange={(e) => { setEntityType(e.target.value); setPage(1); }}
          className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 bg-white text-sm"
        >
          {ENTITY_TYPES.map(e => (
            <option key={e.value} value={e.value}>{e.label}</option>
          ))}
        </select>

        <select
          value={action}
          onChange={(e) => { setAction(e.target.value); setPage(1); }}
          className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 bg-white text-sm"
        >
          {ACTIONS.map(a => (
            <option key={a.value} value={a.value}>{a.label}</option>
          ))}
        </select>
      </div>

      {/* Logs */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            No audit logs found
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {logs.map((log, index) => {
              const EntityIcon = getEntityIcon(log.entity_type);
              
              return (
                <div key={log.id || index} className="p-4 hover:bg-slate-50">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <EntityIcon className="w-5 h-5 text-slate-600" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900">{log.admin_name}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${getActionColor(log.action)}`}>
                          {log.action}
                        </span>
                        <span className="text-slate-500 text-sm">{log.entity_type}</span>
                      </div>
                      
                      {log.details && (
                        <p className="text-sm text-slate-600 mt-1">
                          {Object.entries(log.details).map(([key, value]) => (
                            <span key={key} className="mr-3">
                              <span className="text-slate-400">{key}:</span> {String(value)}
                            </span>
                          ))}
                        </p>
                      )}
                      
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                        <span>{new Date(log.created_at).toLocaleString('en-IN')}</span>
                        {log.ip_address && <span>IP: {log.ip_address}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
          <p className="text-sm text-slate-500">Page {page}</p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={!hasMore}
              className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
